import { useState, useRef } from 'react'
import { Phone, Edit, Trash2, Save, Plus, CheckCircle } from 'lucide-react'
import { X as XIcon } from 'lucide-react'
import { talents, libraries } from '../../services/apiService'
import toast from 'react-hot-toast'
import { TextField, Checkbox, Autocomplete } from '@anyemp/ui-kit'
import SectionBlock from '../UI/SectionBlock'
import SectionHeader from '../UI/SectionHeader'
import ConfirmDialog from '../UI/ConfirmDialog'

const ContactsSection = ({ talent, talentId, hasPermission, setTalent }) => {
  const [contactTools, setContactTools] = useState([])
  const [showContactForm, setShowContactForm] = useState(false)
  const [editingContact, setEditingContact] = useState(null)
  const [contactFormData, setContactFormData] = useState({ tool_id: '', value: '', is_primary: false, is_verified: false })
  const [contactSaving, setContactSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(null) // contact to delete
  const [contactToolsLoading, setContactToolsLoading] = useState(false)
  const contactToolsLoadedRef = useRef(false)

  const loadContactTools = async () => {
    if (contactToolsLoadedRef.current) return
    contactToolsLoadedRef.current = true
    setContactToolsLoading(true)
    try {
      const res = await libraries.getContactTools()
      if (res?.success) setContactTools(Array.isArray(res.data) ? res.data : [])
    } catch (err) {
      console.error('Error loading contact tools:', err)
      contactToolsLoadedRef.current = false
    } finally {
      setContactToolsLoading(false)
    }
  }

  const handleContactEdit = (contact) => {
    loadContactTools()
    setEditingContact(contact)
    setContactFormData({ tool_id: contact.tool_id || '', value: contact.value, is_primary: contact.is_primary, is_verified: contact.is_verified })
    setShowContactForm(true)
  }

  const handleContactAdd = () => {
    loadContactTools()
    setEditingContact(null)
    setContactFormData({ tool_id: '', value: '', is_primary: false, is_verified: false })
    setShowContactForm(true)
  }

  const handleContactCancel = () => {
    setShowContactForm(false)
    setEditingContact(null)
    setContactFormData({ tool_id: '', value: '', is_primary: false, is_verified: false })
  }

  const handleContactSubmit = async (e) => {
    e.preventDefault()
    setContactSaving(true)
    try {
      if (editingContact) {
        const result = await talents.updateTalentContact(talentId, editingContact.id, contactFormData)
        const saved = result?.data || result
        setTalent(prev => ({
          ...prev,
          contacts: (prev.contacts || []).map(c => c.id === editingContact.id ? { ...c, ...contactFormData, ...saved } : c)
        }))
        toast.success('Contact updated', { duration: 2000, position: 'bottom-right' })
      } else {
        const result = await talents.addTalentContact(talentId, contactFormData)
        const saved = result?.data || result
        setTalent(prev => ({ ...prev, contacts: [...(prev.contacts || []), saved] }))
        toast.success('Contact added', { duration: 2000, position: 'bottom-right' })
      }
      handleContactCancel()
    } catch (err) {
      toast.error('Failed to save contact', { duration: 2000, position: 'bottom-right' })
    } finally {
      setContactSaving(false)
    }
  }

  const handleContactDelete = (contact) => {
    setDeleteConfirm(contact)
  }

  const confirmContactDelete = async () => {
    const contact = deleteConfirm
    setContactSaving(true)
    try {
      await talents.deleteTalentContact(talentId, contact.id)
      setTalent(prev => ({ ...prev, contacts: (prev.contacts || []).filter(c => c.id !== contact.id) }))
      toast.success('Contact deleted', { duration: 2000, position: 'bottom-right' })
    } catch (err) {
      toast.error('Failed to delete contact', { duration: 2000, position: 'bottom-right' })
    } finally {
      setContactSaving(false)
    }
  }

  const contactForm = (
    <form key="contact-form" onSubmit={handleContactSubmit} className="p-4 border border-light-border-primary dark:border-dark-border-primary rounded-lg bg-light-bg-tertiary dark:bg-dark-bg-tertiary space-y-3">
      <p className="text-xs font-semibold text-light-text-secondary dark:text-dark-text-secondary uppercase tracking-wide">
        {editingContact ? 'Edit contact' : 'New contact'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-light-text-secondary dark:text-dark-text-secondary mb-1">Type</label>
          <Autocomplete
            clearable
            fullWidth
            value={contactFormData.tool_id ? String(contactFormData.tool_id) : ''}
            onChange={(val) => setContactFormData(prev => ({ ...prev, tool_id: val || '' }))}
            options={contactTools.map(t => ({ value: String(t.id), label: t.name }))}
            placeholder={contactToolsLoading ? 'Loading…' : 'Select type'}
            disabled={contactToolsLoading}
          />
        </div>
        <div>
          <TextField
            label="Value"
            size="small"
            fullWidth
            required
            type="text"
            value={contactFormData.value}
            onChange={(e) => setContactFormData(prev => ({ ...prev, value: e.target.value }))}
            placeholder="Enter contact value"
          />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Checkbox
          label="Primary"
          color="primary"
          size="small"
          checked={!!contactFormData.is_primary}
          onChange={(checked) => setContactFormData(prev => ({ ...prev, is_primary: checked }))}
        />
        <Checkbox
          label="Verified"
          color="primary"
          size="small"
          checked={!!contactFormData.is_verified}
          onChange={(checked) => setContactFormData(prev => ({ ...prev, is_verified: checked }))}
        />
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={contactSaving || contactToolsLoading} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors disabled:opacity-50">
          <Save className="h-3 w-3" /> {editingContact ? 'Update' : 'Add'}
        </button>
        <button type="button" onClick={handleContactCancel} disabled={contactSaving} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-light-border-primary dark:border-dark-border-primary text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary transition-colors disabled:opacity-50">
          <XIcon className="h-3 w-3" /> Cancel
        </button>
      </div>
    </form>
  )

  const cards = (talent.contacts || []).map((contact) => {
    if (editingContact?.id === contact.id) return contactForm
    return (
      <div key={contact.id} className="border border-light-border-primary dark:border-dark-border-primary rounded-lg p-4 bg-light-bg-tertiary dark:bg-dark-bg-tertiary">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {contact.tool?.icon ? (
              <img src={contact.tool.icon} alt={contact.tool.name || ''} className="w-8 h-8 rounded" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }} />
            ) : null}
            <div className={`w-8 h-8 rounded flex items-center justify-center ${contact.tool?.icon ? 'hidden' : 'flex'} bg-gray-100 dark:bg-gray-800`}>
              <Phone className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            </div>
            <div>
              <p className="font-medium text-sm text-light-text-primary dark:text-dark-text-primary">{contact.tool?.name || 'Contact'}</p>
              <button
                onClick={() => navigator.clipboard.writeText(contact.value).then(() => toast.success('Copied!'))}
                className="text-sm text-light-text-secondary dark:text-dark-text-secondary hover:text-primary-600 dark:hover:text-primary-400 transition-colors text-left"
                title="Copy"
              >
                {contact.value}
              </button>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {contact.is_primary && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                <span className="h-1.5 w-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                Primary
              </span>
            )}
            {contact.is_verified && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                <CheckCircle className="h-3 w-3" />
                Verified
              </span>
            )}
            {hasPermission('talent-service', `${talent.talent_type}_contacts`, 'update') && (
              <button onClick={() => handleContactEdit(contact)} onMouseEnter={loadContactTools} onFocus={loadContactTools} disabled={contactSaving} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-primary-600 dark:hover:text-primary-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors disabled:opacity-50" title="Edit">
                <Edit className="h-3.5 w-3.5" />
              </button>
            )}
            {hasPermission('talent-service', `${talent.talent_type}_contacts`, 'delete') && (
              <button onClick={() => handleContactDelete(contact)} disabled={contactSaving} className="p-1.5 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 dark:hover:text-red-400 hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary rounded transition-colors disabled:opacity-50" title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    )
  })

  if (showContactForm && !editingContact) cards.push(contactForm)

  return (
    <>
    <SectionBlock id="contacts">
      <SectionHeader
        icon={Phone}
        title="Contacts"
        action={!showContactForm && hasPermission('talent-service', `${talent.talent_type}_contacts`, 'create') ? (
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-xs font-medium rounded-lg hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors" onClick={handleContactAdd} onMouseEnter={loadContactTools} onFocus={loadContactTools}>
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        ) : null}
      />
      {cards.length > 0
        ? <div className="space-y-3">{cards}</div>
        : <p className="text-sm text-light-text-tertiary dark:text-dark-text-tertiary">No contacts specified</p>
      }
    </SectionBlock>

    <ConfirmDialog
      isOpen={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmContactDelete}
      confirmStyle="danger"
      title="Delete Contact"
      message={deleteConfirm
        ? `Are you sure you want to delete "${deleteConfirm.tool?.name || 'this contact'} — ${deleteConfirm.value}"? This action cannot be undone.`
        : ''}
      confirmText="Delete"
      isLoading={contactSaving}
    />
    </>
  )
}

export default ContactsSection
